import * as Sentry from "@sentry/react";

/**
 * Sentry is initialized as a module side-effect so it runs as soon as this
 * file is imported — before the React app mounts.
 *
 * Active only when VITE_SENTRY_DSN is set AND the Vite build mode is "production".
 * In development (vite dev) all SDK calls are safe no-ops — no events are ever sent.
 */
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

/**
 * Predicates for messages that originate from the host environment (iOS
 * WKWebView internals) rather than MOTUS code. They appear when the app is
 * opened inside an iOS in-app browser / webview and are unactionable noise, so
 * we drop them before they ever reach Sentry.
 *
 * Each predicate is a list of required, WKWebView-specific tokens — the message
 * matches only when ALL tokens are present (case-insensitive). This stays robust
 * to wording, spacing, and punctuation variation (e.g. "did not"/"didn't",
 * optional "this") while keeping `wkwebview` mandatory so genuine app errors are
 * never suppressed.
 */
const NOISE_TOKEN_GROUPS: string[][] = [
  ["wkwebview", "postmessage", "respond"],
];

function isNoiseMessage(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const lower = value.toLowerCase();
  return NOISE_TOKEN_GROUPS.some((tokens) =>
    tokens.every((token) => lower.includes(token)),
  );
}

if (dsn && import.meta.env.MODE === "production") {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [],
    beforeSend(event, hint) {
      const originalError = hint?.originalException;
      if (originalError instanceof Error && isNoiseMessage(originalError.message)) {
        return null;
      }
      if (typeof originalError === "string" && isNoiseMessage(originalError)) {
        return null;
      }
      const exceptionValues = event.exception?.values;
      if (exceptionValues?.some((value) => isNoiseMessage(value.value))) {
        return null;
      }
      if (isNoiseMessage(event.message)) {
        return null;
      }
      return event;
    },
  });
}

export { Sentry };
